import { useEffect, useRef } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Decorative scroll-linked zig-zag particles per section.
const SHAPE_SETS = {
  circles: ["circle", "ring", "circle", "diamond"],
  keys: ["ring", "circle", "diamond", "plus"],
  houses: ["diamond", "plus", "circle", "ring"],
};

function drawShape(ctx, shape, x, y, s) {
  ctx.beginPath();
  if (shape === "circle") {
    ctx.arc(x, y, s, 0, Math.PI * 2);
    ctx.fill();
  } else if (shape === "ring") {
    ctx.arc(x, y, s * 1.4, 0, Math.PI * 2);
    ctx.stroke();
  } else if (shape === "diamond") {
    ctx.moveTo(x, y - s * 1.5);
    ctx.lineTo(x + s * 1.5, y);
    ctx.lineTo(x, y + s * 1.5);
    ctx.lineTo(x - s * 1.5, y);
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.moveTo(x - s * 1.6, y);
    ctx.lineTo(x + s * 1.6, y);
    ctx.moveTo(x, y - s * 1.6);
    ctx.lineTo(x, y + s * 1.6);
    ctx.stroke();
  }
}

// triangular wave in [-1, 1] for a literal zig-zag path
const zig = (t) => {
  const f = t - Math.floor(t + 0.5);
  return Math.abs(4 * f) - 1;
};

export const SectionParticles = ({ variant = "circles", count = 26, className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const shapes = SHAPE_SETS[variant] || SHAPE_SETS.circles;

    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio, 2);
    const particles = Array.from({ length: count }, (_, i) => ({
      bx: Math.random(),
      by: Math.random(),
      amp: 18 + Math.random() * 42,
      freq: 0.4 + Math.random() * 0.9,
      phase: Math.random(),
      drift: 0.25 + Math.random() * 0.75,
      size: 1.2 + Math.random() * 2.2,
      alpha: 0.1 + Math.random() * 0.28,
      shape: shapes[i % shapes.length],
    }));

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    let visible = false;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(canvas);

    let raf;
    let smoothScroll = window.scrollY;
    const render = () => {
      raf = requestAnimationFrame(render);
      if (!visible || !w) return;
      smoothScroll += (window.scrollY - smoothScroll) * 0.06;
      const t = smoothScroll * 0.0012;
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        // vertical drift tied to scroll, wrapped; horizontal zig-zag on the scroll axis
        const y = (((p.by * h - smoothScroll * p.drift * 0.28) % h) + h) % h;
        const x = p.bx * w + p.amp * zig(t * p.freq + p.phase + y * 0.0016);
        ctx.fillStyle = `rgba(148,222,82,${p.alpha})`;
        ctx.strokeStyle = `rgba(148,222,82,${p.alpha})`;
        ctx.lineWidth = 1;
        drawShape(ctx, p.shape, x, y, p.size);
      }
    };
    render();

    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [variant, count]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`fixed inset-0 pointer-events-none ${className}`}
    />
  );
};
